import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  banks,
  emptyAddress,
  insurers,
  products,
  type Address,
  type Bank,
  type BankInput,
  type Insurer,
  type InsurerInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDocument } from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { PartyToolbar } from '../shared/PartyToolbar';

import { InsurerBranchesPanel } from './InsurerBranchesPanel';

const limite = 25;

const situacoesSeguradora = [
  { value: 'ativa', label: 'Ativa' },
  { value: 'em_homologacao', label: 'Em homologacao' },
  { value: 'inativa', label: 'Inativa' },
];

const situacoesBanco = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'em_homologacao', label: 'Em homologacao' },
  { value: 'inativo', label: 'Inativo' },
];

/**
 * Seguradoras.
 *
 * A contraparte nasce em homologacao e so recebe operacao quando fica ativa. A
 * tela mostra a diferenca porque ela decide se a operacao pode ser encaminhada.
 */
export function InsurersPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [criando, setCriando] = useState(false);
  const [emEdicao, setEmEdicao] = useState<Insurer | null>(null);
  // Abas da seguradora aberta: o cadastro e as filiais (P-MIN-2).
  const [abaDaSeguradora, setAbaDaSeguradora] = useState<'cadastro' | 'filiais'>(
    'cadastro',
  );

  const podeCriar = can('seguradoras.seguradora.create');
  const podeAtualizar = can('seguradoras.seguradora.update');

  const consulta = useQuery({
    queryKey: ['insurers', { busca, status, offset }],
    queryFn: ({ signal }) =>
      insurers.list({ q: busca, status, limit: limite, offset }, signal),
  });

  const listaProdutos = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
    enabled: podeCriar || podeAtualizar,
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['insurers'] });
  }

  const criar = useMutation({
    mutationFn: (input: InsurerInput) => insurers.create(input),
    onSuccess: (insurer) => {
      toast.success(`Seguradora ${insurer.legal_name} cadastrada em homologacao.`);
      setCriando(false);
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: InsurerInput }) =>
      insurers.update(id, input),
    onSuccess: (insurer) => {
      toast.success('Seguradora atualizada.');
      setEmEdicao(insurer);
      invalidar();
    },
  });

  const colunas: Column<Insurer>[] = [
    {
      key: 'nome',
      header: 'Seguradora',
      render: (insurer) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{insurer.legal_name}</p>
          <p className="truncate text-2xs text-ink-500">
            {insurer.susep_code ? `SUSEP ${insurer.susep_code}` : 'sem codigo SUSEP'}
          </p>
        </div>
      ),
    },
    {
      key: 'documento',
      header: 'CNPJ',
      hideBelow: 'lg',
      render: (insurer) =>
        insurer.document ? (
          formatDocument(insurer.document)
        ) : (
          <span className="text-ink-400">nao informado</span>
        ),
    },
    {
      key: 'produtos',
      header: 'Produtos',
      align: 'right',
      hideBelow: 'md',
      render: (insurer) => insurer.product_ids.length,
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (insurer) => <StatusBadge kind="contraparte" value={insurer.status} />,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Seguradoras"
        description="Contrapartes que emitem a apolice de garantia."
        actions={
          podeCriar ? (
            <Button onClick={() => setCriando(true)}>
              <Icon name="shield" size={15} />
              Nova seguradora
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar as seguradoras">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por razao social ou codigo SUSEP"
          filter={
            <Select
              label="Situacao"
              className="h-9"
              options={[{ value: '', label: 'Todas' }, ...situacoesSeguradora]}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            />
          }
        />

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(insurer) => insurer.id}
          loading={consulta.isPending}
          onRowClick={(insurer) => {
            setAbaDaSeguradora('cadastro');
            setEmEdicao(insurer);
          }}
          empty={
            <EmptyState
              icon="shield"
              title={
                busca || status
                  ? 'Nenhuma seguradora encontrada'
                  : 'Nenhuma seguradora cadastrada'
              }
              description={
                busca || status
                  ? 'Ajuste a busca ou o filtro de situacao.'
                  : 'A seguradora e quem emite a apolice. Cadastre a primeira para poder encaminhar operacoes.'
              }
              action={
                podeCriar && !busca && !status ? (
                  <Button onClick={() => setCriando(true)}>Cadastrar seguradora</Button>
                ) : null
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal
        open={criando}
        onClose={() => setCriando(false)}
        title="Nova seguradora"
        description="A seguradora nasce em homologacao e passa a ativa quando a habilitacao termina."
        size="lg"
      >
        <InsurerForm
          produtos={listaProdutos.data ?? []}
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>

      <Modal
        open={emEdicao !== null}
        onClose={() => setEmEdicao(null)}
        title={emEdicao?.legal_name ?? ''}
        description={emEdicao ? `Seguradora ${emEdicao.number}` : undefined}
        size="lg"
      >
        {emEdicao ? (
          <div className="flex flex-col gap-4">
            <div
              role="tablist"
              aria-label="Abas da seguradora"
              className="flex gap-1 border-b border-line"
            >
              {(
                [
                  { chave: 'cadastro', rotulo: 'Cadastro' },
                  { chave: 'filiais', rotulo: 'Filiais' },
                ] as const
              ).map((item) => (
                <button
                  key={item.chave}
                  type="button"
                  role="tab"
                  id={`aba-seguradora-${item.chave}`}
                  aria-selected={abaDaSeguradora === item.chave}
                  aria-controls={`painel-seguradora-${item.chave}`}
                  onClick={() => setAbaDaSeguradora(item.chave)}
                  className={
                    abaDaSeguradora === item.chave
                      ? 'border-b-2 border-brand-500 px-3 py-2 text-xs font-semibold text-ink-900'
                      : 'border-b-2 border-transparent px-3 py-2 text-xs text-ink-500 hover:text-ink-900'
                  }
                >
                  {item.rotulo}
                </button>
              ))}
            </div>
            <div
              role="tabpanel"
              id={`painel-seguradora-${abaDaSeguradora}`}
              aria-labelledby={`aba-seguradora-${abaDaSeguradora}`}
            >
              {abaDaSeguradora === 'cadastro' ? (
                <InsurerForm
                  insurer={emEdicao}
                  produtos={listaProdutos.data ?? []}
                  readOnly={!podeAtualizar}
                  submitting={atualizar.isPending}
                  error={atualizar.error}
                  onCancel={() => setEmEdicao(null)}
                  onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
                />
              ) : (
                <InsurerBranchesPanel
                  insurerId={emEdicao.id}
                  readOnly={!podeAtualizar}
                />
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function InsurerForm({
  insurer,
  produtos,
  readOnly,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  insurer?: Insurer | undefined;
  produtos: { id: string; name: string }[];
  readOnly?: boolean | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: InsurerInput) => void;
  onCancel: () => void;
}) {
  const [legalName, setLegalName] = useState(insurer?.legal_name ?? '');
  const [tradeName, setTradeName] = useState(insurer?.trade_name ?? '');
  const [document, setDocument] = useState(insurer?.document ?? '');
  const [susep, setSusep] = useState(insurer?.susep_code ?? '');
  const [status, setStatus] = useState(insurer?.status ?? 'em_homologacao');
  const [email, setEmail] = useState(insurer?.email ?? '');
  const [phone, setPhone] = useState(insurer?.phone ?? '');
  const [contact, setContact] = useState(insurer?.contact_name ?? '');
  const [notes, setNotes] = useState(insurer?.notes ?? '');
  const [selecionados, setSelecionados] = useState<string[]>(
    insurer?.product_ids ?? [],
  );
  // Endereco e atendimento impressos na apolice (P-MIN-2): o rodape e as
  // "Informacoes importantes". So cadastro.
  const [endereco, setEndereco] = useState<Address>(insurer?.address ?? emptyAddress);
  const [site, setSite] = useState(insurer?.website ?? '');
  const [central, setCentral] = useState(insurer?.service_phone ?? '');
  const [ouvidoria, setOuvidoria] = useState(insurer?.ombudsman_phone ?? '');
  const [horario, setHorario] = useState(insurer?.ombudsman_hours ?? '');

  const apiError = error instanceof ApiError ? error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      legal_name: legalName,
      trade_name: tradeName,
      document,
      susep_code: susep,
      status,
      email,
      phone,
      contact_name: contact,
      notes,
      product_ids: selecionados,
      address: endereco,
      website: site,
      service_phone: central,
      ombudsman_phone: ouvidoria,
      ombudsman_hours: horario,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      <fieldset disabled={readOnly || submitting} className="flex flex-col gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Razao social"
            required
            value={legalName}
            error={fieldErrorOf(apiError, 'legal_name')}
            onChange={(event) => setLegalName(event.target.value)}
          />
          <Field
            label="Nome fantasia"
            value={tradeName}
            onChange={(event) => setTradeName(event.target.value)}
          />
          <Field
            label="CNPJ"
            value={document}
            error={fieldErrorOf(apiError, 'document')}
            onChange={(event) => setDocument(event.target.value)}
          />
          <Field
            label="Codigo SUSEP"
            value={susep}
            error={fieldErrorOf(apiError, 'susep_code')}
            hint="De quatro a seis digitos. Opcional durante a homologacao."
            onChange={(event) => setSusep(event.target.value)}
          />
        </div>

        {insurer ? (
          <Select
            label="Situacao"
            options={situacoesSeguradora}
            value={status}
            hint="Somente a seguradora ativa recebe operacao nova."
            onChange={(event) => setStatus(event.target.value as Insurer['status'])}
          />
        ) : null}

        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs font-semibold text-ink-700">
            Produtos que a seguradora aceita
          </legend>
          {produtos.length === 0 ? (
            <p className="text-2xs text-ink-500">
              Nenhum produto cadastrado. Cadastre a arvore de oferta antes de vincular a
              seguradora.
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {produtos.map((produto) => (
                <label
                  key={produto.id}
                  className="flex cursor-pointer items-center gap-2 text-sm text-ink-700"
                >
                  <input
                    type="checkbox"
                    checked={selecionados.includes(produto.id)}
                    onChange={(event) =>
                      setSelecionados((atual) =>
                        event.target.checked
                          ? [...atual, produto.id]
                          : atual.filter((item) => item !== produto.id),
                      )
                    }
                    className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
                  />
                  {produto.name}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Contato"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
          />
          <Field
            label="Email"
            type="email"
            value={email}
            error={fieldErrorOf(apiError, 'email')}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Telefone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        <AddressFields value={endereco} onChange={setEndereco} legend="Endereco" />

        <fieldset className="flex flex-col gap-3">
          <legend className="text-xs font-semibold text-ink-700">Atendimento</legend>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Site"
              value={site}
              maxLength={200}
              error={fieldErrorOf(apiError, 'website')}
              onChange={(event) => setSite(event.target.value)}
            />
            <Field
              label="Central de atendimento"
              value={central}
              maxLength={40}
              onChange={(event) => setCentral(event.target.value)}
            />
            <Field
              label="Telefone da ouvidoria"
              value={ouvidoria}
              maxLength={40}
              onChange={(event) => setOuvidoria(event.target.value)}
            />
            <Field
              label="Horario da ouvidoria"
              value={horario}
              maxLength={200}
              onChange={(event) => setHorario(event.target.value)}
            />
          </div>
        </fieldset>

        <Textarea
          label="Observacoes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </fieldset>

      {!readOnly ? (
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting}>
            {insurer ? 'Salvar alteracoes' : 'Cadastrar'}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

/**
 * Bancos.
 *
 * O banco emite a carta de fianca. Ele e uma contraparte de produto diferente da
 * seguradora, e por isso tem cadastro proprio.
 */
export function BanksPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [criando, setCriando] = useState(false);
  const [emEdicao, setEmEdicao] = useState<Bank | null>(null);

  const podeCriar = can('bancos.banco.create');
  const podeAtualizar = can('bancos.banco.update');

  const consulta = useQuery({
    queryKey: ['banks', { busca, status, offset }],
    queryFn: ({ signal }) =>
      banks.list({ q: busca, status, limit: limite, offset }, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['banks'] });
  }

  const criar = useMutation({
    mutationFn: (input: BankInput) => banks.create(input),
    onSuccess: (bank) => {
      toast.success(`Banco ${bank.name} cadastrado em homologacao.`);
      setCriando(false);
      invalidar();
    },
  });

  const atualizar = useMutation({
    mutationFn: ({ id, input }: { id: string; input: BankInput }) =>
      banks.update(id, input),
    onSuccess: (bank) => {
      toast.success('Banco atualizado.');
      setEmEdicao(bank);
      invalidar();
    },
  });

  const colunas: Column<Bank>[] = [
    {
      key: 'nome',
      header: 'Banco',
      render: (bank) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{bank.name}</p>
          <p className="truncate text-2xs text-ink-500">
            {bank.code ? `Codigo ${bank.code}` : 'sem codigo de compensacao'}
          </p>
        </div>
      ),
    },
    {
      key: 'documento',
      header: 'CNPJ',
      hideBelow: 'lg',
      render: (bank) =>
        bank.document ? (
          formatDocument(bank.document)
        ) : (
          <span className="text-ink-400">nao informado</span>
        ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (bank) => <StatusBadge kind="contraparte" value={bank.status} />,
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Bancos"
        description="Contrapartes que emitem a carta de fianca bancaria."
        actions={
          podeCriar ? (
            <Button onClick={() => setCriando(true)}>
              <Icon name="briefcase" size={15} />
              Novo banco
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os bancos">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por nome ou codigo de compensacao"
          filter={
            <Select
              label="Situacao"
              className="h-9"
              options={[{ value: '', label: 'Todas' }, ...situacoesBanco]}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            />
          }
        />

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(bank) => bank.id}
          loading={consulta.isPending}
          onRowClick={(bank) => setEmEdicao(bank)}
          empty={
            <EmptyState
              icon="briefcase"
              title={
                busca || status ? 'Nenhum banco encontrado' : 'Nenhum banco cadastrado'
              }
              description={
                busca || status
                  ? 'Ajuste a busca ou o filtro de situacao.'
                  : 'O banco emite a carta de fianca. Cadastre o primeiro para operar esse produto.'
              }
              action={
                podeCriar && !busca && !status ? (
                  <Button onClick={() => setCriando(true)}>Cadastrar banco</Button>
                ) : null
              }
            />
          }
        />

        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal open={criando} onClose={() => setCriando(false)} title="Novo banco">
        <BankForm
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>

      <Modal
        open={emEdicao !== null}
        onClose={() => setEmEdicao(null)}
        title={emEdicao?.name ?? ''}
        description={emEdicao ? `Banco ${emEdicao.number}` : undefined}
      >
        {emEdicao ? (
          <BankForm
            bank={emEdicao}
            readOnly={!podeAtualizar}
            submitting={atualizar.isPending}
            error={atualizar.error}
            onCancel={() => setEmEdicao(null)}
            onSubmit={(input) => atualizar.mutate({ id: emEdicao.id, input })}
          />
        ) : null}
      </Modal>
    </div>
  );
}

function BankForm({
  bank,
  readOnly,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  bank?: Bank | undefined;
  readOnly?: boolean | undefined;
  submitting: boolean;
  error: Error | null;
  onSubmit: (input: BankInput) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(bank?.name ?? '');
  const [code, setCode] = useState(bank?.code ?? '');
  const [document, setDocument] = useState(bank?.document ?? '');
  const [status, setStatus] = useState(bank?.status ?? 'em_homologacao');
  const [email, setEmail] = useState(bank?.email ?? '');
  const [phone, setPhone] = useState(bank?.phone ?? '');
  const [contact, setContact] = useState(bank?.contact_name ?? '');
  const [notes, setNotes] = useState(bank?.notes ?? '');

  const apiError = error instanceof ApiError ? error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      name,
      code,
      document,
      status,
      email,
      phone,
      contact_name: contact,
      notes,
    });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      {error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {error.message}
        </Alert>
      ) : null}

      <fieldset disabled={readOnly || submitting} className="flex flex-col gap-4">
        <Field
          label="Nome"
          required
          value={name}
          error={fieldErrorOf(apiError, 'name')}
          onChange={(event) => setName(event.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Codigo de compensacao"
            value={code}
            error={fieldErrorOf(apiError, 'code')}
            hint="Tres digitos, com o zero a esquerda."
            onChange={(event) => setCode(event.target.value)}
          />
          <Field
            label="CNPJ"
            value={document}
            error={fieldErrorOf(apiError, 'document')}
            onChange={(event) => setDocument(event.target.value)}
          />
        </div>

        {bank ? (
          <Select
            label="Situacao"
            options={situacoesBanco}
            value={status}
            hint="Somente o banco ativo recebe operacao nova."
            onChange={(event) => setStatus(event.target.value as Bank['status'])}
          />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Contato"
            value={contact}
            onChange={(event) => setContact(event.target.value)}
          />
          <Field
            label="Email"
            type="email"
            value={email}
            error={fieldErrorOf(apiError, 'email')}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            label="Telefone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </div>

        <Textarea
          label="Observacoes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </fieldset>

      {!readOnly ? (
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" loading={submitting}>
            {bank ? 'Salvar alteracoes' : 'Cadastrar'}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
